import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Paper,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import SalonManagement from './SalonManagement';
import SalonConfiguration from './SalonConfiguration';
import { apiClient } from '../../services/api';

interface DashboardStats {
  totalSalons: number;
  activeSalons: number;
  totalUsers: number;
  totalCustomers: number;
  totalAppointments: number;
  subscriptionBreakdown: {
    basic: number;
    professional: number;
    enterprise: number;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const SuperAdminDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSalonForConfig, setSelectedSalonForConfig] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/tenants?limit=1000');

      if (response.success) {
        const salons = response.data.salons;
        
        const stats: DashboardStats = {
          totalSalons: salons.length,
          activeSalons: salons.filter((s: any) => s.isActive).length,
          totalUsers: salons.reduce((sum: number, s: any) => sum + s._count.users, 0),
          totalCustomers: salons.reduce((sum: number, s: any) => sum + s._count.customers, 0),
          totalAppointments: salons.reduce((sum: number, s: any) => sum + s._count.appointments, 0),
          subscriptionBreakdown: {
            basic: salons.filter((s: any) => s.subscriptionTier === 'basic').length,
            professional: salons.filter((s: any) => s.subscriptionTier === 'professional').length,
            enterprise: salons.filter((s: any) => s.subscriptionTier === 'enterprise').length,
          }
        };
        
        setStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (selectedSalonForConfig) {
    return (
      <SalonConfiguration
        salonId={selectedSalonForConfig}
        onClose={() => setSelectedSalonForConfig(null)}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Super Admin Dashboard
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Platform console: manage tenants and system settings. Open a salon workspace by impersonating an owner from
        Salon Management.
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<DashboardIcon />} label="Overview" />
          <Tab icon={<BusinessIcon />} label="Salon Management" />
          <Tab icon={<SettingsIcon />} label="System Settings" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Overview Dashboard */}
        <Grid container spacing={3}>
          {/* Stats Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {loading ? '...' : stats?.totalSalons || 0}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Salons
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {loading ? '...' : stats?.totalUsers || 0}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Users
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {loading ? '...' : stats?.totalCustomers || 0}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Customers
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {loading ? '...' : stats?.totalAppointments || 0}
                    </Typography>
                    <Typography color="text.secondary">
                      Total Appointments
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Subscription Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Breakdown
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="Basic" color="default" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${stats?.subscriptionBreakdown.basic || 0} Salons`}
                      secondary="Basic tier subscriptions"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="Professional" color="primary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${stats?.subscriptionBreakdown.professional || 0} Salons`}
                      secondary="Professional tier subscriptions"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="Enterprise" color="secondary" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${stats?.subscriptionBreakdown.enterprise || 0} Salons`}
                      secondary="Enterprise tier subscriptions"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* System Health */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Health
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Chip label="Active" color="success" size="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${stats?.activeSalons || 0} Active Salons`}
                      secondary="Currently operational salons"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <StorageIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Database Status"
                      secondary="All systems operational"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUpIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Performance"
                      secondary="System running optimally"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Salon Management */}
        <SalonManagement />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* System Settings */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Settings
            </Typography>
            <Typography color="text.secondary">
              System-wide configuration options will be available here.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default SuperAdminDashboard;